/**
 * Element Block Types
 * Embedded content types that directly reuse existing element data schemas
 *
 * This module provides BlockContent as a discriminated union that embeds
 * the actual element data types, ensuring full compatibility with existing elements.
 */

// Import actual element data types
import type { TextElementData } from '../text/schema';
import type { ImageElementData } from '../image/schema';
import type { AudioElementData } from '../audio/schema';
import type { VideoElementData } from '../video/schema';
import type { RichTextElementData } from '../rich-text/schema';

/**
 * Supported content types (matches element module names)
 */
export type BlockContentType = 'text' | 'image' | 'audio' | 'video' | 'rich-text' | 'office-document';

/** Alignment for block content (text, image) */
export type BlockContentAlignment = 'left' | 'center' | 'right';

/**
 * Text block - embeds TextElementData with type discriminator
 */
export interface TextBlockContent extends TextElementData {
    type: 'text';
}

/**
 * Image block - embeds ImageElementData with type discriminator
 */
export interface ImageBlockContent extends ImageElementData {
    type: 'image';
}

/**
 * Audio block - embeds AudioElementData with type discriminator
 */
export interface AudioBlockContent extends AudioElementData {
    type: 'audio';
}

/**
 * Video block - embeds VideoElementData with type discriminator
 */
export interface VideoBlockContent extends VideoElementData {
    type: 'video';
}

/**
 * Rich text block - embeds RichTextElementData with type discriminator
 */
export interface RichTextBlockContent extends RichTextElementData {
    type: 'rich-text';
}

/**
 * Office / PDF document block (in-browser viewers in player)
 */
export interface OfficeDocumentBlockContent {
    type: 'office-document';
    /** Public URL to PDF or Office file */
    src: string;
    /** Optional heading shown above the viewer */
    title?: string;
    /** Original file name (display) */
    fileName?: string;
}

/**
 * Union type for all block content types
 */
export type BlockContent =
    | TextBlockContent
    | ImageBlockContent
    | AudioBlockContent
    | VideoBlockContent
    | RichTextBlockContent
    | OfficeDocumentBlockContent;

/**
 * Check if block content is empty (no visible content to display)
 */
export function isEmptyBlockContent(content: BlockContent | undefined): boolean {
    if (!content || typeof content !== 'object') return true;
    switch (content.type) {
        case 'text':
            return !(content.text ?? '').trim();
        case 'image':
            return !(content.src ?? '').trim();
        case 'audio':
            return !(content.src ?? '').trim();
        case 'video':
            return !(content.src ?? '').trim();
        case 'office-document':
            return !(content.src ?? '').trim();
        case 'rich-text': {
            const inner = content.content;
            if (inner == null) return true;
            if (typeof inner === 'string') return inner.trim() === '';
            if (
                typeof inner === 'object' &&
                'markdown' in inner &&
                typeof (inner as { markdown: string }).markdown === 'string'
            )
                return (inner as { markdown: string }).markdown.trim() === '';
            if (
                typeof inner === 'object' &&
                'type' in inner &&
                inner.type === 'doc' &&
                Array.isArray((inner as { content?: unknown[] }).content)
            ) {
                const nodes = (inner as { content: Array<{ type?: string; content?: Array<{ type?: string; text?: string }> }> })
                    .content;
                if (nodes.length === 0) return true;
                const hasContent = nodes.some((node) => {
                    if (node.type === 'image') return true;
                    const innerContent = node.content ?? [];
                    return innerContent.some((c) => c.type === 'text' && (c as { text?: string }).text?.trim());
                });
                return !hasContent;
            }
            return true;
        }
        default:
            return true;
    }
}

/**
 * Helper to get preview text from block content
 */
export function getBlockPreview(content: BlockContent | undefined): string {
    if (!content) return '';
    switch (content.type) {
        case 'text':
            return content.text || '';
        case 'image':
            return content.alt || '[Image]';
        case 'audio':
            return content.title || '[Audio]';
        case 'video':
            return content.title || '[Video]';
        case 'office-document':
            return content.title?.trim() || content.fileName?.trim() || '[Document]';
        case 'rich-text': {
            const inner = content.content;
            if (typeof inner === 'string') return inner.slice(0, 100);
            if (
                inner &&
                typeof inner === 'object' &&
                'markdown' in inner &&
                typeof (inner as { markdown: string }).markdown === 'string'
            )
                return (inner as { markdown: string }).markdown.slice(0, 100);
            const doc = inner as { content?: Array<{ type?: string; content?: Array<{ text?: string }> }> };
            const firstNode = doc?.content?.[0];
            const text = firstNode?.content?.[0]?.text;
            if (text) return text.slice(0, 100);
            return '[Rich Text]';
        }
        default:
            return '';
    }
}

export interface CreateDefaultBlockContentOptions {
    alignment?: BlockContentAlignment;
}

/**
 * Helper to create default content of a type (with full element defaults)
 */
export function createDefaultBlockContent(
    type: BlockContentType,
    options?: CreateDefaultBlockContentOptions
): BlockContent {
    const alignment = options?.alignment;
    switch (type) {
        case 'text':
            return {
                type: 'text',
                text: '',
                variant: 'body',
                alignment: alignment ?? 'left',
            };
        case 'image':
            return {
                type: 'image',
                src: '',
                alt: '',
                caption: '',
                size: 'medium',
                alignment: alignment ?? 'center',
            };
        case 'audio':
            return {
                type: 'audio',
                src: '',
                title: '',
                autoplay: false,
                loop: false,
                showControls: true,
            };
        case 'video':
            return {
                type: 'video',
                src: '',
                title: '',
                poster: '',
                autoplay: false,
                loop: false,
                muted: false,
                showControls: true,
                aspectRatio: '16:9',
            };
        case 'rich-text':
            return {
                type: 'rich-text',
                content: { type: 'doc', content: [{ type: 'paragraph' }] },
            };
        case 'office-document':
            return {
                type: 'office-document',
                src: '',
                title: '',
                fileName: '',
            };
        default:
            return {
                type: 'text',
                text: '',
                variant: 'body',
                alignment: alignment ?? 'left',
            };
    }
}

/**
 * Helper to get display content (with fallback to text)
 */
export function getDisplayBlockContent(
    content: BlockContent | undefined,
    fallbackText: string
): BlockContent {
    if (content) return content;
    return { type: 'text', text: fallbackText, variant: 'body', alignment: 'left' };
}

/**
 * Helper to extract plain text from content (for compatibility)
 */
export function getPlainText(content: BlockContent | undefined): string {
    if (!content) return '';
    if (content.type === 'text') return content.text;
    return getBlockPreview(content);
}

/**
 * Convert BlockContent to element data (removes type discriminator)
 */
export function toTextElementData(content: TextBlockContent): TextElementData {
    const { type, ...data } = content;
    return data;
}

export function toImageElementData(content: ImageBlockContent): ImageElementData {
    const { type, ...data } = content;
    return data;
}

export function toAudioElementData(content: AudioBlockContent): AudioElementData {
    const { type, ...data } = content;
    return data;
}

export function toVideoElementData(content: VideoBlockContent): VideoElementData {
    const { type, ...data } = content;
    return data;
}

export function toRichTextElementData(content: RichTextBlockContent): RichTextElementData {
    const { type, ...data } = content;
    return data;
}

/**
 * Convert element data to BlockContent (adds type discriminator)
 */
export function fromTextElementData(data: TextElementData): TextBlockContent {
    return { type: 'text', ...data };
}

export function fromImageElementData(data: ImageElementData): ImageBlockContent {
    return { type: 'image', ...data };
}

export function fromAudioElementData(data: AudioElementData): AudioBlockContent {
    return { type: 'audio', ...data };
}

export function fromVideoElementData(data: VideoElementData): VideoBlockContent {
    return { type: 'video', ...data };
}

export function fromRichTextElementData(data: RichTextElementData): RichTextBlockContent {
    return { type: 'rich-text', ...data };
}

// Re-export element data types for consumers
export type {
    TextElementData,
    ImageElementData,
    AudioElementData,
    VideoElementData,
    RichTextElementData,
};
